import type { DefaultTheme } from 'styled-components';
import { DateTime } from 'luxon';
import type { TimeSeriesPoint } from '../../../../shared/charts/types';
import { getForecastDayChartDomain } from '../../helpers/day/chartDomain';
import type { WeatherForecastRiskLevel } from '../../helpers/risk/riskLevels';

export { getForecastDayChartDomain };

export const RISK_BAR_HEIGHT = 14;

/** Matches Figma Group 399 side-band height (Rectangle 197). */
export const RISK_CHART_PLOT_HEIGHT = 49;

export interface RiskBarSegment {
  startTime: Date;
  endTime: Date;
  riskLevel: WeatherForecastRiskLevel;
}

export interface RiskScrubPoint {
  time: Date;
  riskLevel: WeatherForecastRiskLevel;
}

export const getRiskLevelColor = (
  riskLevel: WeatherForecastRiskLevel,
  theme: DefaultTheme,
): string => {
  switch (riskLevel) {
    case 'danger':
      return theme.colors.danger;
    case 'warning':
      return theme.colors.warning;
    case 'caution':
      return theme.colors.success;
    default:
      return theme.colors.darkgrey;
  }
};

/** All the bar needs is when the hour was and how it rated. */
export interface RiskGraphPointForScrub {
  x: string;
  riskLevel: WeatherForecastRiskLevel;
}

export const riskGraphPointsToScrubSeries = (
  points: RiskGraphPointForScrub[],
  timeZone: string,
): RiskScrubPoint[] =>
  points
    .map((point) => {
      const pointDateTime = DateTime.fromISO(point.x, { zone: timeZone });
      if (!pointDateTime.isValid) {
        return null;
      }

      return {
        time: pointDateTime.toJSDate(),
        riskLevel: point.riskLevel,
      };
    })
    .filter((point): point is RiskScrubPoint => point != null)
    .sort((a, b) => a.time.getTime() - b.time.getTime());

export const riskScrubPointsToTimeSeries = (
  points: RiskScrubPoint[],
): TimeSeriesPoint[] =>
  points.map((point) => ({
    time: point.time,
    value: 1,
  }));

const midpointTime = (left: Date, right: Date): Date =>
  new Date((left.getTime() + right.getTime()) / 2);

/**
 * Tiles the chart domain with hourly slices (each hour centred in its slice) and
 * merges consecutive equal risk levels so the bar is continuous with no gaps.
 */
export const buildRiskBarSegments = (
  points: RiskScrubPoint[],
  domain: { start: Date; end: Date },
): RiskBarSegment[] => {
  if (points.length === 0) {
    return [];
  }

  if (points.length === 1) {
    return [
      {
        startTime: domain.start,
        endTime: domain.end,
        riskLevel: points[0].riskLevel,
      },
    ];
  }

  const slices: RiskBarSegment[] = points.map((point, index) => ({
    startTime:
      index === 0
        ? domain.start
        : midpointTime(points[index - 1].time, point.time),
    endTime:
      index === points.length - 1
        ? domain.end
        : midpointTime(point.time, points[index + 1].time),
    riskLevel: point.riskLevel,
  }));

  const merged: RiskBarSegment[] = [];

  slices.forEach((slice) => {
    const previous = merged[merged.length - 1];

    if (previous && previous.riskLevel === slice.riskLevel) {
      previous.endTime = slice.endTime;
      return;
    }

    merged.push({ ...slice });
  });

  return merged;
};

export const getRiskLevelAtIndex = (
  points: RiskScrubPoint[],
  selectedIndex: number,
): WeatherForecastRiskLevel | undefined => {
  if (selectedIndex < 0 || selectedIndex >= points.length) {
    return undefined;
  }

  return points[selectedIndex].riskLevel;
};
